import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { saveAs } from 'file-saver';

import api from '../../../utils/functions/api';
import IdCardIndicator from './IdCardIndicator';
import SsoPreviewTable from './SsoPreviewTable';
import { SSO_GROUP_COLORS, SSO_GROUP_LABELS } from './constants';
import { monthLabel, stripEmbeddedTitle } from './utils';

// จำไว้ว่า HR ติ๊กออก (ยกเลิกเลือก) ใครไว้บ้างต่อเดือน — เก็บเฉพาะ "รายชื่อที่ถูกติ๊กออก" ไม่ใช่
// รายชื่อที่เลือกทั้งหมด เพราะ default คือเลือกทุกคนอยู่แล้ว เก็บแค่ข้อยกเว้นเบากว่า และรายชื่อ
// พนักงานที่เพิ่มเข้ามาใหม่ทีหลัง (คนละเดือน key คนละอัน) จะเป็น "เลือกไว้" โดยอัตโนมัติเสมอ
const storageKeyFor = (year, month) => `sso_export_unchecked_${year}_${month}`;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);

// ล็อกแถวสรุปไว้ล่างสุดของ TableContainer เสมอ (position: sticky บน cell โดยตรง เหมือน pattern
// ที่ stickyHeader ของ MUI Table ใช้กับ th — ใส่ bgcolor ทึบแสงจริงกันเนื้อหาที่ scroll ผ่านทะลุ
// ออกมาให้เห็น เหตุผลเดียวกับ sticky column ในตารางอื่นๆ ของแอป)
const stickyFooterCellSx = {
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderTop: '2px solid',
  borderColor: 'divider',
  fontWeight: 700,
};

export default function SsoExportModal({ open, onClose, year, month }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [previewMode, setPreviewMode] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setPreviewMode(false);
    setLoading(true);
    api
      .get(`${process.env.REACT_APP_API_URL}/monthly-salary-summary/${year}/${month}/sso-export`)
      .then(({ data }) => {
        const list = data || [];
        setRows(list);
        let uncheckedIds = [];
        try {
          uncheckedIds = JSON.parse(localStorage.getItem(storageKeyFor(year, month)) || '[]');
        } catch {
          uncheckedIds = [];
        }
        const uncheckedSet = new Set(uncheckedIds.map(String));
        setSelected(new Set(list.filter((r) => !uncheckedSet.has(String(r.id))).map((r) => r.id)));
      })
      .finally(() => setLoading(false));
  }, [open, year, month]);

  // บันทึกรายชื่อที่ถูกติ๊กออกลง localStorage ทุกครั้งที่เปลี่ยนการเลือก (ไม่ critical ถ้า
  // localStorage เต็ม/ถูกบล็อก แค่จำสถานะไม่ได้ ไม่กระทบการใช้งานหลัก)
  const persistUnchecked = (nextSelected) => {
    try {
      const uncheckedIds = rows.filter((r) => !nextSelected.has(r.id)).map((r) => r.id);
      localStorage.setItem(storageKeyFor(year, month), JSON.stringify(uncheckedIds));
    } catch {
      // เก็บสถานะไม่ได้ก็ปล่อยผ่าน
    }
  };

  const toggleOne = (rowId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      persistUnchecked(next);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id));
      persistUnchecked(next);
      return next;
    });
  };

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);
  const missingIdSelectedCount = selectedRows.filter((r) => !r.has_id_card).length;
  // สรุปยอดเฉพาะคนที่ติ๊กเลือกไว้ (ตรงกับที่จะอยู่ในไฟล์จริงถ้ากดดาวน์โหลดตอนนี้) — อัปเดตสดตาม
  // การติ๊ก/ยกเลิกแต่ละแถว ไม่ใช่ยอดรวมทั้งหมดที่โหลดมาเฉยๆ
  const totalSalary = useMemo(() => selectedRows.reduce((sum, r) => sum + (r.salary || 0), 0), [selectedRows]);
  const totalSso = useMemo(() => selectedRows.reduce((sum, r) => sum + (r.sso || 0), 0), [selectedRows]);
  const withSsoCount = useMemo(() => selectedRows.filter((r) => (r.sso || 0) > 0).length, [selectedRows]);

  const fileName = `4.ไฟล์นำส่ง ปกส.เดือน${monthLabel(year, month)} บจก.โซตัส.xlsx`;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/monthly-salary-summary/${year}/${month}/sso-export/download`,
        { ids: Array.from(selected) },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, fileName);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" fontWeight={700}>ส่งออกไฟล์นำส่ง ปกส.</Typography>
            <Typography variant="body2" color="text.secondary">{monthLabel(year, month)}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {loading ? (
          <Skeleton variant="rounded" width="100%" height={300} animation="wave" />
        ) : previewMode ? (
          <SsoPreviewTable rows={selectedRows} />
        ) : (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Checkbox
                  size="small"
                  checked={rows.length > 0 && selected.size === rows.length}
                  indeterminate={selected.size > 0 && selected.size < rows.length}
                  onChange={toggleAll}
                />
                <Typography variant="body2">เลือกทั้งหมด/ยกเลิกทั้งหมด</Typography>
              </Stack>
              <Chip size="small" label={`เลือก ${selected.size} จาก ${rows.length} คน`} color="primary" variant="outlined" />
            </Stack>
            {missingIdSelectedCount > 0 && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                มี {missingIdSelectedCount} คนที่เลือกไว้ยังไม่มีเลขบัตรประชาชนในระบบ — ช่องเลขบัตรจะเว้นว่างไว้ในไฟล์
              </Typography>
            )}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell>คำนำหน้า</TableCell>
                    <TableCell>ชื่อ</TableCell>
                    <TableCell>กลุ่ม</TableCell>
                    <TableCell align="right">ค่าจ้างเดือน</TableCell>
                    <TableCell align="right">เงินสมทบ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => toggleOne(row.id)}
                      sx={{ cursor: 'pointer', bgcolor: alpha(SSO_GROUP_COLORS[row.group] || SSO_GROUP_COLORS.FULLTIME, 0.35) }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox size="small" checked={selected.has(row.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleOne(row.id)} />
                      </TableCell>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IdCardIndicator hasIdCard={row.has_id_card} />
                          {stripEmbeddedTitle(row.firstname)} {row.lastname}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={SSO_GROUP_LABELS[row.group] || row.group}>
                          <Chip size="small" label={SSO_GROUP_LABELS[row.group]?.split(' ')[0] || row.group} />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(row.salary)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(row.sso)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4} sx={stickyFooterCellSx}>
                      {`รวม (${selectedRows.length} คน · มีเงินสมทบ ${withSsoCount} คน)`}
                    </TableCell>
                    <TableCell align="right" sx={{ ...stickyFooterCellSx, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(totalSalary)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...stickyFooterCellSx, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(totalSso)}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {previewMode ? (
          <Button onClick={() => setPreviewMode(false)}>กลับไปเลือกรายชื่อ</Button>
        ) : (
          <>
            <Button onClick={onClose}>ยกเลิก</Button>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon fontSize="small" />}
              onClick={() => setPreviewMode(true)}
              disabled={selected.size === 0}
            >
              ดูตัวอย่าง
            </Button>
          </>
        )}
        <Button
          variant="contained"
          startIcon={<DownloadIcon fontSize="small" />}
          onClick={handleDownload}
          disabled={selected.size === 0 || downloading}
        >
          {downloading ? 'กำลังสร้างไฟล์...' : 'ดาวน์โหลด Excel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
